import React, { useState, useEffect, useRef } from 'react';
import '../../../styles/homepagecss/SLR.css';

const HomeSLR = () => {
  const [phase, setPhase] = useState(-1);
  const [isPaused, setIsPaused] = useState(false);
  const [counters, setCounters] = useState({});
  const [resetKey, setResetKey] = useState(0);
  const timerRef = useRef(null);
  const sectionRef = useRef(null);

  const reviewers = [
    { id: 1, name: 'Dr. Sarah Chen', initials: 'SC' },
    { id: 2, name: 'Dr. Michael Torres', initials: 'MT' },
    { id: 3, name: 'Dr. Emma Wilson', initials: 'EW' },
  ];

  const studies = [
    { title: 'Pediatric Population: Retrospective Cohort', ai: 'exclude', human: 'include' },
    { title: 'Treatment Adherence: Meta-Analysis', ai: 'include', human: 'include' },
    { title: 'Quality of Life in Rare Disorders', ai: 'include', human: 'include' }
  ];

  // Intersection Observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => entry.isIntersecting && setPhase(0),
      { threshold: 0.2 }
    );
    sectionRef.current && observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  // Animation Controller
  useEffect(() => {
    if (phase === -1 || isPaused) return;

    const sequence = [
      1000,  // 0: Card 1 appears
      1000, // 1: Reviewers animate
      100,  // 2: Card 2 appears
      1000, // 3: AI papers
      1000, // 4: Human papers
      1000, // 5: Conflict badge
      100,  // 6: Card 3 appears
      1000, // 7: PDFs
      1000, // 8: Reviewers Phase 2
      100,  // 9: Card 4 appears
      1000, // 10: AI analysis
      1000, // 11: Human validation
      1000, // 12: Final badge
      5000  // 13: Pause before loop
    ];

    timerRef.current = setTimeout(() => {
      if (phase === 13) {
        setCounters({});
        setResetKey(prev => prev + 1);
        setPhase(-1);
        setTimeout(() => setPhase(0), 100);
      } else {
        setPhase(phase + 1);
      }
    }, sequence[phase]);

    return () => clearTimeout(timerRef.current);
  }, [phase, isPaused]);

  const Counter = ({ end, show, id, counters, setCounters }) => {
    const [count, setCount] = useState(0);

    useEffect(() => {
      if (counters[id] !== undefined) {
        setCount(counters[id]);
        return;
      }
      
      if (!show) {
        setCount(0);
        return;
      }
      
      let start = 0;
      const duration = 1000;
      const increment = end / (duration / 16);
      
      const timer = setInterval(() => {
        start += increment;
        if (start >= end) {
          setCount(end);
          setCounters(prev => ({ ...prev, [id]: end }));
          clearInterval(timer);
        } else {
          setCount(Math.floor(start));
        }
      }, 16);

      return () => clearInterval(timer);
    }, [show, end, id, counters, setCounters]);

    return counters[id] !== undefined ? counters[id] : count;
  };

  return (
    <section className="background-color2" ref={sectionRef}>
      <div className="container">
        {/* Header */}
        <p className="ev-reg-p-dark ev-fs-20 ev-bold-med mb-1">SLR AI Studio.</p>
        <p className="gradient-font7 ev-fs-20 margin-bottom-3  ev-bold-med">
          AI Screens at Scale, Experts Validate with Precision.
        </p>

        {/* Features */}
        <div className="row g-0 margin-bottom-3">
          {[
            { icon: 'bi-robot', title: 'Dual AI & Human Screening', desc: '  Start human validation while AI completes the remaining corpus' },
            { icon: 'bi-shield-check', title: 'Conflict Resolution', desc: 'Discrepancies identified. Side-by-side comparison. Full audit trail.' },
            { icon: 'bi-people-fill', title: 'Multi-Reviewer Assignment', desc: 'Assign your systematic review team phase wise.' },
            { icon: 'bi-diagram-3-fill', title: 'PRISMA Compliant', desc: 'Flow diagrams generated automatically. Export-ready phase tables.' },
            { icon: 'bi-people-fill', title: 'RAG-Powered Intelligence', desc: 'Documents preprocessed for intelligent retrieval.' },
            { icon: 'bi-diagram-3-fill', title: 'Progress & Status Tracking', desc: ' One-glance execution awareness across all modules.' }
          ].map((f, i) => (
            <div key={i} className="col-lg-2 col-md-4 col-6 g-0">
                <div class="row g-0">
                  <div class="col-10 p-0">
                    <p className="ev-fs-14 ev-reg-p-dark ev-bold-med mb-1 slr-feature-card-title">{f.title}</p>
                    <p className="ev-fs-11 ev-reg-p-dark ev-reg mb-0">{f.desc}</p>
                  </div>
                </div>
            </div>
          ))}
        </div>

        {/* Pause/Play 
        <div className="text-center mb-4">
          <button className="ev-btn-blue-outline-round ev-btn-small ev-bold-reg" onClick={() => setIsPaused(!isPaused)}>
            <i className={`bi bi-${isPaused ? 'play' : 'pause'}-fill me-1`}></i>
            {isPaused ? 'Resume' : 'Pause'}
          </button>
        </div>*/}

        {/* 4 Cards - Bootstrap Grid */}
        <div className="row g-2">
          
          {/* CARD 1 */}
          <div className={`col-lg-3 col-md-6 col-6 slr-card-animate ${phase >= 0 ? 'show' : ''}`}>
            <div className="ev-card-4 ev-padding-small h-100">
              <div className="slr-card-head">
                <div className="slr-num">1</div>
                <div>
                  <div className="slr-label">SLR FABRIC SETUP</div>
                  <div className="slr-title">Configure Your SLR Project</div>
                </div>
              </div>
              <div className="pt-2">
                {/* PICOS */}
                <div className={`slr-picos mb-3 ${phase >= 0 ? 'show' : ''}`}>
                  <div className="slr-picos-item inc">
                    <i className="bi bi-check-circle-fill"></i>
                    <span><b>Inclusion Criteria.</b> RCTs 2015-2025</span>
                  </div>
                  <div className="slr-picos-item exc">
                    <i className="bi bi-x-circle-fill"></i>
                    <span> <b>Exclusion Criteria.</b> Animal studies</span>
                  </div>
                </div>

                {/* CSV */}
                <div className={`slr-csv mb-3 ${phase >= 0 ? 'show' : ''}`}>
                 <i class="bi bi-list-columns-reverse"></i>
                  <div>
                    <div className="slr-csv-name">literature_records_2025.csv</div>
                    <div className="slr-csv-bar"></div>
                    <div className="ev-reg-p-blue ev-bold-reg">
                      <Counter 
                        key={`csv-${resetKey}`} 
                        end={2847} 
                        show={phase >= 0} 
                        id="csv" 
                        counters={counters}
                        setCounters={setCounters}
                      /> Literatures
                    </div>
                  </div>
                </div>

                {/* Reviewers */}
                <div className="slr-team">
                  <div className="slr-team-label">
                    <i className="bi bi-people-fill me-2"></i>Assign Phase 1 Reviewers
                  </div>
                  {reviewers.map((r, i) => (
                    <div key={r.id} className={`slr-reviewer ${phase >= 1 ? 'show' : ''}`} style={{ animationDelay: `${i * 0.1}s` }}>
                      <div className="slr-avatar">{r.initials}</div>
                      <div className="slr-name">{r.name}</div>
                      <i className="bi bi-check-circle-fill ev-fs-16 text-primary"></i>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* CARD 2 */}
          <div className={`col-lg-3 col-md-6 col-6 slr-card-animate ${phase >= 2 ? 'show' : ''}`}>
            <div className="ev-card-4 ev-padding-small h-100">
              <div className="slr-card-head">
                <div className="slr-num">2</div>
                <div>
                  <div className="slr-label">PHASE 1 SCREENING</div>
                  <div className="slr-title">Title & Abstract Review</div>
                </div>
              </div>
              <div className="pt-2">
                {/* AI */}
                <div className="slr-review mb-3">
                  <div className="slr-review-head ai">
                    <i className="bi bi-robot"></i>
                    <span>AI Screening</span>
                  </div>
                  {studies.map((s, i) => (
                    <div key={i} className={`slr-paper ${phase >= 3 ? 'scan' : ''}`} style={{ animationDelay: `${i * 0.4}s` }}>
                      <div className="slr-paper-title">{s.title}</div>
                      <div className={`slr-decision ${s.ai}`}>
                        <i className={`bi bi-${s.ai === 'include' ? 'check' : 'x'}-circle-fill`}></i>
                        {s.ai === 'include' ? 'Include' : 'Exclude'}
                      </div>
                    </div>
                  ))}
                  <div className={`slr-stats ${phase >= 3 ? 'show' : ''}`} style={{ animationDelay: '1s' }}>
                    <span className="ev-bold-med">485/2847</span>
                  </div>
                </div>

                {/* Human */}
                <div className="slr-review mb-3">
                  <div className="slr-review-head human">
                    <i className="bi bi-person-check-fill"></i>
                    <span>Human Review</span>
                  </div>
                  {studies.map((s, i) => (
                    <div key={i} className={`slr-paper ${phase >= 4 ? 'scan' : ''}`} style={{ animationDelay: `${i * 0.4}s` }}>
                      <div className="slr-paper-title">{s.title}</div>
                      <div className={`slr-decision ${s.human}`}>
                        <i className={`bi bi-${s.human === 'include' ? 'check' : 'x'}-circle-fill`}></i>
                        {s.human === 'include' ? 'Include' : 'Exclude'}
                      </div>
                    </div>
                  ))}
                  <div className={`slr-stats ${phase >= 4 ? 'show' : ''}`} style={{ animationDelay: '1s' }}>
                    <span className="ev-bold-med">489/2847</span>
                  </div>
                </div>

                {/* CONFLICT */}
                <div className={`slr-conflict ${phase >= 5 ? 'show' : ''} mb-2`}>
                  <i className="bi bi-exclamation-triangle-fill"></i>
                  <span>4 conflicts • Resolution required</span>
                </div>
                <div className={`slr-final ${phase >= 5 ? 'show' : ''}`}>
                  <i className="bi bi-check-circle-fill"></i>
                  <span>487 studies in final synthesis</span>
                </div>
              </div>
            </div>
          </div>

          {/* CARD 3 */}
          <div className={`col-lg-3 col-md-6 col-6 slr-card-animate ${phase >= 6 ? 'show' : ''}`}>
            <div className="ev-card-4 ev-padding-small h-100">
              <div className="slr-card-head">
                <div className="slr-num">3</div>
                <div>
                  <div className="slr-label">DATA COLLECTION & PHASE 2 SETUP</div>
                  <div className="slr-title">Full-Text Preparation</div>
                </div>
              </div>
              <div className="pt-3">
                {/* PDF Upload */}
                <div className={`slr-pdf-upload mb-3 ${phase >= 7 ? 'show' : ''}`}>
                  <div className="slr-doc-list-icon">
                    <i class="bi bi-list-columns-reverse"></i>
                  </div>
                  <div>
                    <div className="ev-fs-12 ev-bold-med ev-reg-p-dark">Upload PDFs to Data Collection</div>
                    <div className="ev-reg-p-blue ev-bold-reg">
                      <Counter 
                        key={`pdf-${resetKey}`} 
                        end={487} 
                        show={phase >= 7} 
                        id="pdf" 
                        counters={counters}
                        setCounters={setCounters}
                      /> documents
                    </div>
                  </div>
                </div>

                {/* PDF Grid */}
                <div className="slr-pdf-grid mb-3">
                  {[...Array(24)].map((_, i) => (
                    <div key={i} className={`slr-pdf-item ${phase >= 7 ? 'show' : ''}`} style={{ animationDelay: `${i * 0.06}s` }}>
                      <i className="bi bi-file-pdf-fill"></i>
                    </div>
                  ))}
                </div>

                {/* Data Collection Link */}
                <div className={`slr-link mb-3 ${phase >= 7 ? 'show' : ''}`} style={{ animationDelay: '0.5s' }}>
                  <i className="bi bi-link-45deg"></i>
                  <span>Documents attached to SLR Project</span>
                </div>

                {/* Reviewers */}
                <div className="slr-team">
                  <div className="slr-team-label">
                    <i className="bi bi-people-fill me-2"></i>Assign Phase 2 Reviewers
                  </div>
                  {reviewers.map((r, i) => (
                    <div key={r.id} className={`slr-reviewer ${phase >= 8 ? 'show' : ''}`} style={{ animationDelay: `${i * 0.1}s` }}>
                      <div className="slr-avatar">{r.initials}</div>
                      <div className="slr-name">{r.name}</div>
                      <i className="bi bi-check-circle-fill ev-fs-16 text-primary"></i>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* CARD 4 */}
          <div className={`col-lg-3 col-md-6 col-6 slr-card-animate ${phase >= 9 ? 'show' : ''}`}>
            <div className="ev-card-4 ev-padding-small h-100">
              <div className="slr-card-head">
                <div className="slr-num">4</div>
                <div>
                  <div className="slr-label">PHASE 2 SCREENING</div>
                  <div className="slr-title">Full-Text Review</div>
                </div>
              </div>
              <div className="">
                {/* AI */}
                <div className="slr-review mb-3">
                  <div className="slr-review-head ai">
                    <i className="bi bi-robot"></i>
                    <span>AI Deep Analysis</span>
                  </div>
                  {studies.map((s, i) => (
                    <div key={i} className={`slr-paper pdf ${phase >= 10 ? 'scan' : ''}`} style={{ animationDelay: `${i * 0.4}s` }}>
                      <div className="slr-pdf-thumb">
                        <i className="bi bi-file-pdf-fill"></i>
                      </div>
                      <div>
                        <div className="slr-paper-title">Literature{i + 1}.pdf</div>
                        <div className="slr-paper-sub">{s.title}</div>
                      </div>
                      <div className={`slr-decision ${s.ai}`}>
                        <i className={`bi bi-${s.ai === 'include' ? 'check' : 'x'}-circle-fill`}></i>
                      </div>
                    </div>
                  ))}
                  <div className={`slr-stats ${phase >= 10 ? 'show' : ''}`} style={{ animationDelay: '1s' }}>
                    <span className="ev-bold-med">285/487</span>
                  </div>
                </div>

                {/* Human */}
                <div className="slr-review mb-3">
                  <div className="slr-review-head human">
                    <i className="bi bi-person-check-fill"></i>
                    <span>Human Validation</span>
                  </div>
                  {studies.map((s, i) => (
                    <div key={i} className={`slr-paper pdf ${phase >= 11 ? 'scan' : ''}`} style={{ animationDelay: `${i * 0.4}s` }}>
                      <div className="slr-pdf-thumb">
                        <i className="bi bi-file-pdf-fill"></i>
                      </div>
                      <div>
                        <div className="slr-paper-title">Literature{i + 1}.pdf</div>
                        <div className="slr-paper-sub">{s.title}</div>
                      </div>
                      <div className={`slr-decision ${s.human}`}>
                        <i className={`bi bi-${s.human === 'include' ? 'check' : 'x'}-circle-fill`}></i>
                      </div>
                    </div>
                  ))}
                  <div className={`slr-stats ${phase >= 11 ? 'show' : ''}`} style={{ animationDelay: '1s' }}>
                    <span className="ev-bold-med">290/487</span>
                  </div>
                </div>

                {/* FINAL */}
                <div className={`slr-conflict ${phase >= 12 ? 'show' : ''} mb-2`}>
                  <i className="bi bi-exclamation-triangle-fill"></i>
                  <span>5 conflicts • Resolution required</span>
                </div>
                <div className={`slr-final ${phase >= 12 ? 'show' : ''}`}>
                  <i className="bi bi-check-circle-fill"></i>
                  <span>289 studies in final synthesis</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HomeSLR;